// แถบซ้ายของหน้าแบรนด์ (แทน renderSidebar() เดิม)
import { Link, useNavigate } from "react-router";
import type { Brand } from "@contracts/api";
import { useBrandTodayCount } from "@features/inbox/api/use-brand-pages";
import { IconChevronDown, IconCollapse } from "@shared/ui/icons";
import { MENU_ICONS } from "@shared/ui/menu-icons";
import { menuPath, visibleMenus } from "../model/side-menus";
import { useSidebarStore } from "../model/sidebar-store";
import { BrandSwitcher } from "./brand-switcher";
import { cn } from "@shared/lib/utils";

type Props = {
  brand: Brand;
  brands: Brand[];
  activeMenuId: string;
  activeSubSlug?: string;
};

export function Sidebar({ brand, brands, activeMenuId, activeSubSlug }: Props) {
  const navigate = useNavigate();
  const collapsed = useSidebarStore((s) => s.collapsed);
  const collapsedMenus = useSidebarStore((s) => s.collapsedMenus);
  const toggleCollapsed = useSidebarStore((s) => s.toggleCollapsed);
  const toggleMenu = useSidebarStore((s) => s.toggleMenu);
  const todayCount = useBrandTodayCount(brand);

  return (
    <aside className={cn("sidebar", collapsed && "collapsed")} id="sidebar">
      <div className="sidebar-inner">
        <BrandSwitcher brand={brand} brands={brands} />
        <nav className="side-nav">
          {visibleMenus(brand).map((m) => {
            const hasSub = m.submenu.length > 0;
            const isActive = m.id === activeMenuId;
            // เมนูที่ active ต้องกางเสมอ แม้ผู้ใช้เคยหุบไว้ (ไม่งั้นมองไม่เห็นว่าอยู่หน้าไหน)
            const expanded = hasSub && (isActive || !collapsedMenus.includes(m.id));
            const Icon = MENU_ICONS[m.icon];

            return (
              <div
                key={m.id}
                className={cn("side-item", isActive && "active", expanded && "expanded")}
                data-menu={m.id}
                data-has-sub={hasSub}
              >
                <button
                  type="button"
                  className="side-link"
                  onClick={() => {
                    // มีเมนูย่อย = กดที่หัวข้อเพื่อกาง/หุบ ไม่ใช่เข้าหน้า
                    // (ตอนแถบหุบอยู่ไม่มีที่ให้กาง จึงเข้าหน้าเลย เหมือนของเดิม)
                    if (hasSub && !collapsed) toggleMenu(m.id);
                    else navigate(menuPath(brand.id, m.id));
                  }}
                >
                  <span className="icon">
                    <Icon />
                  </span>
                  <span className="label">{m.label}</span>
                  {m.id === "message" && (
                    <span className="side-count" hidden={!todayCount}>
                      {todayCount || ""}
                    </span>
                  )}
                  {hasSub && <IconChevronDown />}
                </button>
                {hasSub && (
                  /* ต้องเป็น <a> ไม่ใช่ <button> เพราะ CSS เดิมจัดสไตล์ที่ .submenu a */
                  <div className="submenu">
                    {m.submenu.map((s) => (
                      <Link
                        key={s.slug}
                        to={menuPath(brand.id, m.id, s.slug)}
                        className={isActive && activeSubSlug === s.slug ? "active" : ""}
                      >
                        {s.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>
      <div className="sidebar-footer">
        <button type="button" className="collapse-btn" onClick={toggleCollapsed}>
          <span className="icon">
            <IconCollapse />
          </span>
          <span className="label">Collapse</span>
        </button>
      </div>
    </aside>
  );
}
